<!--
 * COPYRIGHT Transtron Inc 2023
 *
 * 機能名：ユーザ情報一覧画面
 *
 * 作成者：2023/06/19 ノア）馬
 * 最終修正者：2023/06/19 ノア）馬
 *
 -->
<template>
  <div class="tti-main-view">
    <!-- ヘッダー -->
    <section>
      <nav class="tti-main-header-shadow">
        <div class="pull-left">
          <el-button
            class="backButton"
            @click="onBackButtonClick"
            v-tooltip="{
              content: `${$t('gpsTracker.tooltipCommon.companyInfo.back')}`,
              placement: 'bottom-start',
              delay: { show: 1000, hide: 0 }
            }"
          >
            <tti-svg-icon icon-name="wo_034"></tti-svg-icon>
          </el-button>
          <div class="tti-viewTitle">
            <tti-svg-icon icon-name="wo_008"></tti-svg-icon>
            <span>{{ $t('gpsTracker.userInfo.title') }}</span>
          </div>
        </div>
        <div class="pull-right">
          <div class="searchBox">
            <el-input
              v-model.trim="searchText"
              onkeyup="this.value=this.value.replace(/[,]/g,'')"
              v-tooltip="{
                content: `${$t('gpsTracker.tooltipCommon.userInfo.textFieldSearch')}`,
                placement: 'bottom-start',
                delay: { show: 1000, hide: 0 }
              }"
            ></el-input>
            <!-- 検索 -->
            <el-button
              class="tti-btn-default tti-btn-002"
              @click="onSearchBoxClick"
              v-tooltip="{
                content: `${$t('gpsTracker.tooltipCommon.userInfo.btnSearch')}`,
                placement: 'bottom-start',
                delay: { show: 1000, hide: 0 }
              }"
            >
              {{ $t('buttons.search') }}
            </el-button>
            <!-- 全表示 -->
            <el-button
              class="tti-btn-default tti-btn-002"
              @click="onAllDisplayClick"
              v-tooltip="{
                content: `${$t('gpsTracker.tooltipCommon.userInfo.btnViewAll')}`,
                placement: 'bottom-start',
                delay: { show: 1000, hide: 0 }
              }"
            >
              {{ $t('buttons.allDisplay') }}
            </el-button>
          </div>
        </div>
      </nav>
    </section>
    <!-- テーブル -->
    <section class="tti-padding-content">
      <el-table
        ref="tableRef"
        :data="tableData"
        height="calc(100vh - 180px)"
        :border="true"
        :stripe="true"
        :row-class-name="tableRowClassName"
        header-row-class-name="tti-master-table-header"
        :empty-text="$t('messages.common.emptyTableData')"
        highlight-current-row
        @current-change="handleCurrentChange"
      >
        <!-- ユーザID-->
        <el-table-column prop="userId" :label="$t('gpsTracker.userInfo.userId')" width="160"> </el-table-column>
        <!-- ユーザ名称 -->
        <el-table-column prop="userName" :label="$t('gpsTracker.userInfo.userName')" width="160"> </el-table-column>
        <!-- 会社コード -->
        <el-table-column prop="companyCode" :label="$t('gpsTracker.userInfo.companyCode')" width="160">
        </el-table-column>
        <!-- ユーザ種類 -->
        <el-table-column prop="role" :label="$t('gpsTracker.userInfo.role')" width="160">
          <template slot-scope="scope">
            {{ getUserRoleText(scope.row.role) }}
          </template>
        </el-table-column>
        <!-- ユーザ類別 -->
        <el-table-column prop="adminFlg" :label="$t('gpsTracker.userInfo.adminFlg')" width="160">
          <template slot-scope="scope">
            {{ getUserTypeText(scope.row.adminFlg) }}
          </template>
        </el-table-column>
      </el-table>
    </section>
    <!-- フッター -->
    <section class="tti-master-bottom-area">
      <div class="tti-bottom-left-area">
        <!-- 追加 -->
        <el-button
          class="tti-btn-default tti-btn-005"
          @click="onAddClick"
          v-tooltip="{
            content: `${$t('gpsTracker.tooltipCommon.userInfo.btnAdd')}`,
            placement: 'bottom-start',
            delay: { show: 1000, hide: 0 }
          }"
          >{{ $t('buttons.insert') }}</el-button
        >
        <!-- 編集 -->
        <el-button
          class="tti-btn-default tti-btn-005"
          @click="onEditClick"
          :disabled="isBTNDisabled"
          v-tooltip="{
            content: `${$t('gpsTracker.tooltipCommon.userInfo.btnEdit')}`,
            placement: 'bottom-start',
            delay: { show: 1000, hide: 0 }
          }"
        >
          {{ $t('buttons.edit') }}
        </el-button>
        <!-- 削除 -->
        <el-button
          class="tti-btn-default tti-btn-002"
          @click="onDeleteClick"
          :disabled="isBTNDisabled"
          v-tooltip="getTooltip(tooltipType.delete)"
        >
          {{ $t('buttons.delete') }}
        </el-button>
      </div>
    </section>
    <ToolUserInfoAddUpdate
      :companyId="companyId"
      :companyPrefix="companyPrefix"
      :dataType="dataType"
      :singleSelection="singleSelection"
      :maxCount="tableData.length"
      :singleSelectionIndex="singleSelectionIndex"
      @updateSuccess="updateSuccess"
      @stepPrevFunction="stepPrevFunction"
      @stepNextFunction="stepNextFunction"
      @dialogClose="dialogClose"
    ></ToolUserInfoAddUpdate>
  </div>
</template>

<script lang="ts">
import { Component, Ref } from 'vue-property-decorator'
import { Table } from 'element-ui'

import TOOL_COMPANY_BASE from '@/views/companyEditTool/TOOL_COMPANY_BASE.vue'
import { i18n } from '@/locales'
import {
  CompanyAddToolData,
  ToolUserInfoData,
  ToolUserInfoGetParam,
  CompanyGeneral,
  ToolUserInfoDeleteParam
} from '@/models'
import { deleteUserInfo, getToolUserInfoData } from '@/store/apis'
import { msgBoxService } from '@/commons/services/msgBoxService'
import { companyGeneralService, CompanyGeneralType } from '@/commons/services/companyGeneralService'
import { dialog } from '@/store/modules'
import { DataType, AdminFlgType } from '@/commons/constants'
import config from '@/config/config'

@Component({
  name: 'TOOL_USER_INFO',
  components: { ToolUserInfoAddUpdate: () => import('@/components/ToolUserInfoAddUpdate.vue') }
})
export default class TOOL_USER_INFO extends TOOL_COMPANY_BASE {
  @Ref('tableRef') readonly tableRef!: Table

  /**
   * 画面ID
   */
  pageId = 'TOOL_USER_INFO'

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  scope: any

  /**
   * 検索文字列
   */
  searchText = ''

  /**
   * 変更したデータ
   */
  tableData: Array<ToolUserInfoData> = []

  /**
   * テーブル行で選択したデータ
   */
  singleSelection: ToolUserInfoData = {} as ToolUserInfoData

  /**
   * テーブル行で選択したデータINDEX
   */
  singleSelectionIndex = -1

  /**
   * ボタンのアクティブ状態
   */
  isBTNDisabled = true

  /**
   * 会社情報ID
   */
  companyId = ''

  /**
   * 契約会社Prefix名
   */
  companyPrefix = ''

  /**
   * ユーザ種類リスト
   */
  userRoleList: CompanyGeneral[] = []

  /**
   * ユーザ類別リスト
   */
  userTypeList: CompanyGeneral[] = [
    { name: '管理者', code: AdminFlgType.SYSTEM },
    { name: '利用者', code: AdminFlgType.USER }
  ]

  /**
   * 追加、編集
   */
  dataType = DataType.INSERT

  /**
   * インスタンスがマウントされたちょうど後にフック処理
   *
   * @returns {void}
   */
  async mounted(): Promise<void> {
    this.$log.debug('TOOL_USER_INFO > mounted')
    this.initConfig()
    if (!this.routeQueryCheck()) {
      return
    }
    this.fetchUserRole()
    this.fetchTableData()
  }

  /**
   * 関連データの取得
   */
  initConfig(): void {
    this.companyId = this.$route.query.companyId as string
    this.companyPrefix = this.$route.query.companyPrefix as string
  }

  /**
   * ユーザ種類nameを取得する。
   *
   * @param {string} role ユーザ種類code
   * @returns {string} ユーザ種類name
   */
  getUserRoleText(role: string): string {
    if (!this.userRoleList || this.userRoleList.length === 0) return ''

    const userRole = this.userRoleList.filter(item => item.code === role)[0]
    if (!userRole) return ''
    return userRole.name
  }

  /**
   * ユーザ類別nameを取得する。
   *
   * @param {string} userTypeCode ユーザ類別code
   * @returns {string} ユーザ類別name
   */
  getUserTypeText(userTypeCode: string): string {
    if (!this.userTypeList || this.userTypeList.length === 0) return ''
    const userType = this.userTypeList.filter(item => item.code === userTypeCode)[0]
    if (!userType) return ''
    return userType.name
  }

  fetchTableDataBefore(): () => Promise<CompanyAddToolData> {
    return () => {
      const param = {
        companyId: this.companyId,
        userIdOrUserName: this.searchText
      } as ToolUserInfoGetParam
      return getToolUserInfoData(param)
    }
  }

  /**
   * テーブルデータを検索して、初期化を実行する。
   *
   * @param {boolean} isBTNDisabled ボタン活性値
   * @returns {Promise<boolean>} 検索実行結果
   */
  async fetchTableData(isBTNDisabled = true): Promise<boolean> {
    try {
      this.$log.debug(`TOOL_USER_INFO > fetchTableData`)
      const data = (await this.fetchTableDataBefore()()) as CompanyAddToolData
      this.initTableData(data.list as Array<ToolUserInfoData>, isBTNDisabled)
      await this.fetchTableDataAfter()
      return true
      /* eslint-disable  @typescript-eslint/no-unused-vars */
      /* eslint-disable  @typescript-eslint/no-explicit-any */
    } catch (e: any) {
      if (e?.data?.common?.code === 'E00101') {
        this.$router.push(config.toolMenu.companyInfo)
        return false
      }

      this.initTableData([])
      this.$log.debug(`TOOL_USER_INFO > fetchTableData: e=`, e)
      throw e
    }
  }

  /**
   * テーブルデータの初期化を実行する。
   *
   * @param {Array<ToolUserInfoData>} data 検索されたデータ
   * @param {boolean} isBTNDisabled ボタン活性値
   * @returns {void}
   */
  initTableData(data: Array<ToolUserInfoData>, isBTNDisabled = true): void {
    if (!data) {
      data = []
    }

    this.tableData = data

    this.isBTNDisabled = isBTNDisabled
  }

  /**
   * 検索後に実行したい操作。
   *
   * @returns {void}
   */
  fetchTableDataAfter(): void {
    this.$log.debug('TOOL_USER_INFO > fetchTableDataAfter ')

    if (this.tableData && this.tableData.length > 0 && this.singleSelectionIndex !== -1) {
      this.$nextTick(() => {
        this.singleSelection = this.tableData[this.singleSelectionIndex]
        this.isBTNDisabled = false
      })
    }
  }

  /**
   * パラメータ着信がない場合
   *
   * @returns {boolean} 関連結菓
   */
  routeQueryCheck(): boolean {
    if (!this.companyId) {
      msgBoxService.info(i18n.t('vaildate.common.E00092').toString(), () => {
        this.$router.push(config.toolMenu.companyInfo)
      })
      return false
    }
    return true
  }

  /**
   * 検索BOXをクリックしたときの処理
   *
   * @returns {void}
   */
  onSearchBoxClick(): void {
    this.fetchTableData()
    this.singleSelectionIndex = -1
    this.singleSelection = {} as ToolUserInfoData
    this.isBTNDisabled = true
  }

  /**
   * 全表示
   *
   */
  onAllDisplayClick(): void {
    this.$log.debug('TOOL_USER_INFO > onAllDisplayClick ')
    this.searchText = ''
    this.fetchTableData()
    this.singleSelectionIndex = -1
    this.singleSelection = {} as ToolUserInfoData
    this.isBTNDisabled = true
  }

  /**
   * 登録
   *
   */
  onAddClick(): void {
    this.$log.debug('TOOL_USER_INFO > onAddClick ')
    this.dataType = DataType.INSERT
    dialog.setToolUserAddUpdateShow(true)
  }

  /**
   * 編集
   *
   */
  onEditClick(): void {
    this.$log.debug('TOOL_USER_INFO > onEditClick ')
    this.dataType = DataType.UPDATE
    dialog.setToolUserAddUpdateShow(true)
  }

  /**
   * ダイアログを閉じた後にトリガーされる関数
   *
   * @param {boolean} showStatus ダイアログボックスの表示状態
   */
  dialogClose(showStatus: boolean): void {
    this.$log.debug(`TOOL_USER_INFO >  dialogClose `)
    if (showStatus) {
      this.initConfig()
      this.singleSelectionIndex = -1
      this.singleSelection = {} as ToolUserInfoData
      this.isBTNDisabled = true
      this.searchText = ''
      this.fetchTableData()
    }
  }

  /**
   * ダイアログボックスで次の操作で格納された場所が正常に処理されました
   *
   * @param {number} index インデックス
   */
  updateSuccess(index: number): void {
    this.$log.debug(`TOOL_USER_INFO >  updateSuccess `, dialog.isToolUserAddUpdateDialogShow)
    this.initConfig()
    if (index === -1) {
      this.fetchTableData()
      this.singleSelectionIndex = -1
      this.singleSelection = {} as ToolUserInfoData
    } else {
      this.fetchTableData(false)
    }

    if (this.tableData && this.tableData.length > index && index >= 0) {
      this.$nextTick(() => {
        this.singleSelection = this.tableData[index]
        this.singleSelectionIndex = index
      })
    }
  }

  /**
   * テーブルの選択したデータを変更する。
   *
   * @param {ToolUserInfoData} row 選択されたデータ
   */
  handleCurrentChange(row: ToolUserInfoData): void {
    this.$log.debug(`TOOL_USER_INFO >  handleCurrentChange,`, row)
    if (row) {
      this.singleSelection = row
      this.singleSelectionIndex = this.tableData.findIndex(item => item.id === row.id)
      this.isBTNDisabled = false
    } else {
      if (
        (dialog.isToolUserAddUpdateDialogShow && this.dataType == DataType.UPDATE) ||
        this.singleSelectionIndex !== -1
      ) {
        this.isBTNDisabled = false
      } else {
        this.isBTNDisabled = true
        this.singleSelectionIndex = -1
      }
    }
  }

  /**
   * 選択した行の設定
   *
   * @param {number} currentIndex 行索引の選択
   */
  stepPrevFunction(currentIndex: number): void {
    this.$log.debug(`TOOL_USER_INFO >  stepPrevFunction,`, currentIndex)
    this.initConfig()

    const changedIndex = currentIndex - 1
    this.changeSelectedRow(changedIndex)
  }

  /**
   * 選択した行の設定
   *
   * @param {number} currentIndex 行索引の選択
   */
  stepNextFunction(currentIndex: number): void {
    this.$log.debug(`TOOL_USER_INFO >  stepNextFunction,`, currentIndex)
    this.initConfig()

    const changedIndex = currentIndex + 1
    this.changeSelectedRow(changedIndex)
  }

  /**
   * 選択した行を実施
   *
   * @param {number} selectRowIndex 行索引の選択
   */
  changeSelectedRow(selectRowIndex: number): void {
    this.fetchTableData(false)
    if (this.tableData && this.tableData.length > selectRowIndex && selectRowIndex >= 0) {
      this.singleSelection = this.tableData[selectRowIndex]

      this.$nextTick(() => {
        this.singleSelectionIndex = selectRowIndex
      })
    }
  }

  /**
   * テーブル行CLASS
   *
   * @param {any} rowData 対象データ
   * @returns {string} 目標CLASS
   */
  /* eslint-disable @typescript-eslint/no-explicit-any */
  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  tableRowClassName(rowData: any): string {
    const { rowIndex } = rowData
    if (this.singleSelectionIndex === -1) {
      return ''
    }

    if (this.singleSelectionIndex === rowIndex) {
      return 'current-row'
    }

    return ''
  }

  /**
   * 削除
   *
   */
  onDeleteClick(): void {
    this.$log.debug('TOOL_USER_INFO > onDeleteClick ')
    const confirmCallback = () => {
      this.doDeleteUserInfo()
      this.singleSelectionIndex = -1
    }

    msgBoxService.confirm(i18n.t('messages.masterCommon.deleteConfirm').toString(), confirmCallback)
  }

  /**
   * ユーザ削除実行
   */
  async doDeleteUserInfo(): Promise<void> {
    if (this.singleSelection && this.companyId) {
      const param = {
        id: this.singleSelection.id,
        companyId: this.companyId
      } as ToolUserInfoDeleteParam

      await deleteUserInfo(param)

      this.fetchTableData()
    }
  }

  /**
   * 戻るボタンをクリックした時の処理。
   *
   * @returns {void}
   */
  onBackButtonClick(): void {
    this.$log.debug(`TOOL_USER_INFO > onBackButtonClick`)

    this.doBack()
  }

  /**
   * 前画面へ戻る
   *
   * @returns {void}
   */
  doBack(): void {
    this.$log.debug(`TOOL_USER_INFO > doBack`)
    this.$router.back()
  }

  /**
   * ユーザ種類リストを取得する
   *
   * @returns {void}
   */
  async fetchUserRole(): Promise<void> {
    try {
      this.userRoleList = await companyGeneralService[this.companyId][CompanyGeneralType.USER_ROLE]
    } catch (e) {
      this.$log.debug('TOOL_USER_INFO > fetchUserRole: e=', e)
      throw e
    }
  }
}
</script>

<style lang="scss" scoped>
::v-deep .tti-bottom-right-area .tti-btn-default {
  margin-right: 0 !important;
}

.tti-main-header-shadow {
  display: flex;
  flex-direction: row;
  justify-content: space-between;
}

.searchBox {
  display: flex;
  align-items: center;

  ::v-deep .el-input__inner {
    height: 35px;
    line-height: 35px;
    padding: 0 8px;
  }
}
</style>
